import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import type { Exercise, GradeResult, Response } from '@yuan-math/exercises';
import { grade, misconception } from '@yuan-math/exercises';
import { Button, Confetti, Figure, Mascot, Math } from '@yuan-math/ui';
import { Link } from 'react-router-dom';
import { registry } from '../state/LearnerContext.tsx';

/**
 * One exercise, from question to explanation.
 *
 * The contract is deliberately narrow: this component knows how to *ask* and
 * how to *show a result*. It does not know what a fraction is, how to grade
 * one, or what a misconception means — all of that lives in the packages, so
 * the same runner serves practice, lessons and the diagnostic.
 *
 * Answering follows the four-part explanation from §3: what went wrong, why,
 * what concept is missing, and how to do it.
 */
export function ExerciseRunner({
  exercise,
  onGraded,
  onNext,
  showNext,
  autoFocus = true,
  final = false,
}: {
  exercise: Exercise;
  /** Fired once per *attempt*, including retries. */
  onGraded?: (result: GradeResult, meta: { responseMs: number; firstTry: boolean; usedHint: boolean }) => void;
  onNext?: () => void;
  showNext?: boolean;
  autoFocus?: boolean;
  /**
   * The first graded answer is the last one: no second try, the working shown
   * at once, and the way onwards offered immediately.
   *
   * For the placement test, where a retry would be a lie. The ladder has
   * already recorded the first answer and re-planned around it, so a second
   * attempt cannot change anything — and the ordinary flow would *require* one
   * before releasing the Next button, which turns "I cannot do this" into two
   * forced guesses. Everywhere else a second try is the teaching, and this stays
   * off.
   */
  final?: boolean;
}): JSX.Element {
  const [text, setText] = useState('');
  const [choiceId, setChoiceId] = useState<string | null>(null);
  const [result, setResult] = useState<GradeResult | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [hintShown, setHintShown] = useState(false);
  const startedAt = useRef(Date.now());
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset every piece of per-question state when the question changes.
  useEffect(() => {
    setText('');
    setChoiceId(null);
    setResult(null);
    setAttempts(0);
    setHintShown(false);
    startedAt.current = Date.now();
    if (autoFocus) inputRef.current?.focus();
  }, [exercise.id, autoFocus]);

  const isChoice = exercise.type === 'multiple-choice' || exercise.type === 'multi-select';
  const solved = result?.correct === true;
  const answered = result !== null && result.status !== 'malformed';
  /** Nothing more to type: right, or wrong and final. */
  const locked = solved || (final && answered);
  /** The question is over — reveal the working and offer the way onwards. */
  const settled = answered && (final || result.correct || attempts >= 2);

  const submit = (response: Response): void => {
    if (locked) return;
    const graded = grade(exercise, response);
    setResult(graded);

    // A malformed answer is not an attempt — it does not count against the
    // student, and it does not advance the retry counter.
    if (graded.status === 'malformed') return;

    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);
    onGraded?.(graded, {
      responseMs: Date.now() - startedAt.current,
      firstTry: nextAttempts === 1,
      usedHint: hintShown,
    });
  };

  /**
   * iOS answers `inputMode="decimal"` with a bare 0-9 keypad: no minus sign,
   * and no slash. On a phone that makes a negative answer — or any fraction —
   * literally untypable, so the characters the keypad leaves out get their own
   * buttons. They are always shown: offering the sign key only when the answer
   * happens to be negative would give the sign away.
   */
  const numericKeypad = exercise.type !== 'expression';
  const needsSlash = exercise.type === 'fraction' || exercise.answer.kind === 'fraction';

  /** Calculator behaviour: the sign key flips the whole answer, not a digit. */
  const toggleSign = (): void => {
    setText((current) => (current.startsWith('-') ? current.slice(1) : `-${current}`));
    inputRef.current?.focus();
  };

  const insertAtCaret = (chunk: string): void => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? text.length;
    setText(text.slice(0, start) + chunk + text.slice(end));
    // The caret otherwise jumps to the end on the controlled re-render.
    const caret = start + chunk.length;
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  };

  const named = result?.misconceptionId ? misconception(result.misconceptionId) : undefined;
  const remediation = named ? registry.concept(named.remediationConceptId) : undefined;

  return (
    <section className={`exercise ${solved ? 'exercise--solved' : ''}`} aria-live="polite">
      <div className="exercise__question">
        <p className="exercise__prompt">{exercise.question.prompt}</p>
        {exercise.question.latex ? <Math latex={exercise.question.latex} display /> : null}
        {exercise.question.figure ? (
          <div className="exercise__figure">
            <Figure figure={exercise.question.figure} />
          </div>
        ) : null}
      </div>

      {isChoice ? (
        <div className="choices" role="radiogroup" aria-label="Answer choices">
          {(exercise.question.choices ?? []).map((choice) => {
            const chosen = choiceId === choice.id;
            const isAnswer = exercise.answer.kind === 'choice' && exercise.answer.correctId === choice.id;
            const state = result && chosen ? (result.correct ? 'correct' : 'incorrect') : '';
            const reveal = result && !result.correct && isAnswer ? 'reveal' : '';
            return (
              <button
                key={choice.id}
                type="button"
                role="radio"
                aria-checked={chosen}
                disabled={locked}
                className={`choice ${chosen ? 'choice--chosen' : ''} ${state ? `choice--${state}` : ''} ${reveal ? 'choice--reveal' : ''}`}
                onClick={() => {
                  setChoiceId(choice.id);
                  submit({ kind: 'choice', id: choice.id });
                }}
              >
                {choice.latex ? <Math latex={choice.latex} /> : choice.label}
              </button>
            );
          })}
        </div>
      ) : (
        <form
          className="answer-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit({ kind: 'text', value: text });
          }}
        >
          <label className="answer-form__label" htmlFor={`answer-${exercise.id}`}>
            Your answer
          </label>
          <div className="answer-form__row">
            <input
              id={`answer-${exercise.id}`}
              ref={inputRef}
              className={`answer-input ${result && result.status !== 'malformed' ? (result.correct ? 'answer-input--correct' : 'answer-input--incorrect') : ''}`}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={placeholderFor(exercise)}
              disabled={locked}
              autoComplete="off"
              inputMode={exercise.type === 'expression' ? 'text' : 'decimal'}
            />
            {exercise.question.unit ? <span className="answer-unit">{exercise.question.unit}</span> : null}
            <Button type="submit" disabled={locked || text.trim() === ''}>
              Check
            </Button>
          </div>
          {numericKeypad ? (
            <div className="answer-keys">
              <button
                type="button"
                className="answer-key"
                onClick={toggleSign}
                disabled={locked}
                aria-label="Negative sign"
              >
                &#8722;
              </button>
              {needsSlash ? (
                <button
                  type="button"
                  className="answer-key"
                  onClick={() => insertAtCaret('/')}
                  disabled={locked}
                  aria-label="Fraction bar"
                >
                  /
                </button>
              ) : null}
            </div>
          ) : null}
        </form>
      )}

      {!locked && exercise.explanation.hint ? (
        <div className="hint-row">
          {hintShown ? (
            <p className="hint">
              <span className="hint__label">Hint</span> {exercise.explanation.hint}
            </p>
          ) : (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setHintShown(true)}>
              Show a hint
            </button>
          )}
        </div>
      ) : null}

      {result ? (
        <div className={`feedback feedback--${result.status}`}>
          {/* The mascot reacts before the words are read. It is the fastest
              channel there is for a child who is still learning to read, and
              on a wrong answer it is sympathetic rather than cross. */}
          <div className="feedback__lead">
            <Mascot className="feedback__mascot" mood={moodFor(result.status)} size={64} />
            <div>
              <p className="feedback__headline">{headlineFor(result.status)}</p>
              {/* The grader's sentence is only worth showing when it says more
                  than the headline already did — a bare "Correct." twice is
                  noise. */}
              {result.feedback && result.feedback !== headlineFor(result.status) ? (
                <p className="feedback__body">{result.feedback}</p>
              ) : null}
            </div>
          </div>

          {named && remediation ? (
            <p className="feedback__remediation">
              This usually means one idea needs another look:{' '}
              <Link to={`/concepts/${remediation.id}`}>{remediation.title}</Link>.
            </p>
          ) : null}

          {settled ? (
            <details className="explanation" open={!result.correct}>
              <summary>How it works</summary>
              <p className="explanation__summary">{exercise.explanation.summary}</p>
              {exercise.explanation.steps ? (
                <ol className="steps steps--compact">
                  {exercise.explanation.steps.map((step, index) => (
                    <li key={index} className="step">
                      <div className="step__label">{step.label}</div>
                      <Math latex={step.latex} display />
                      {step.note ? <p className="step__note">{step.note}</p> : null}
                    </li>
                  ))}
                </ol>
              ) : null}
              {!result.correct ? (
                <p className="explanation__answer">
                  The answer is <strong>{result.expected}</strong>.
                </p>
              ) : null}
            </details>
          ) : null}

          {!final && !result.correct && result.status !== 'malformed' && attempts === 1 ? (
            <p className="feedback__retry">Have another go — you get a second try before the answer is shown.</p>
          ) : null}

          {/* Onwards lives in the band, beside the reason to go onwards. A
              child following the green never has to look elsewhere. */}
          {showNext && settled ? (
            <div className="feedback__actions">
              <Button onClick={onNext} size="lg">
                Next question
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Only on a first-time correct answer. Confetti for a second attempt,
          or for re-reading a solved question, is congratulation nobody earned. */}
      {solved && attempts === 1 ? <Confetti /> : null}
    </section>
  );
}

/** Which face the mascot pulls. Never a cross one — see the band's comment. */
function moodFor(status: GradeResult['status']): 'happy' | 'sad' | 'thinking' {
  switch (status) {
    case 'correct':
      return 'happy';
    case 'malformed':
      return 'thinking';
    default:
      return 'sad';
  }
}

function headlineFor(status: GradeResult['status']): string {
  switch (status) {
    case 'correct':
      return 'Correct.';
    case 'malformed':
      return 'I could not read that.';
    default:
      return 'Not quite.';
  }
}

function placeholderFor(exercise: Exercise): string {
  switch (exercise.answer.kind) {
    case 'fraction':
      return 'e.g. 3/4';
    case 'expression':
      return 'e.g. 2n + 5';
    default:
      return 'Type a number';
  }
}
